import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { API, img } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import useSEO from "@/hooks/useSEO";

const FABRIC_NOTES = {
  Muslin: "Feather-light & breathable",
  Rayon: "Fluid drape, soft touch",
  Crepe: "Textured, flowy elegance",
  "Turkish Slub Cotton": "Everyday comfort, honest texture",
};

export default function Home() {
  const { fmt } = useStore();
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  useSEO({
    title: "Just Kords — Co-ord Sets for Every Mood | justkords.com",
    description: "Stylish co-ord sets for women in luxe linen, silk satin, tailored blazers and fine knits. Free shipping in India above ₹2,999. Razorpay secured payments & 14-day returns.",
  });

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const tagged = products.filter((p) => p.featured || (p.tags || []).includes("Bestseller"));
  const bestsellers = (tagged.length ? tagged : products).slice(0, 8);
  const fabrics = ["Muslin", "Rayon", "Crepe", "Turkish Slub Cotton"];
  const bentoTiles = [
    ...fabrics.filter((f) => products.some((p) => p.fabric === f)).map((f, i) => ({
      label: f, note: FABRIC_NOTES[f] || "", to: `/shop?fabric=${encodeURIComponent(f)}`,
      img: ((products.find((p) => p.fabric === f) || {}).images || [])[0],
      span: i === 0 ? "md:col-span-2 md:row-span-2" : "",
    })),
    { label: "Shop All", note: "The full collection", to: "/shop", img: ((products[0] || {}).images || [])[0], span: "" },
  ];
  const fallbackWall = ["photo-1763971922545-2e5ed772ae43", "photo-1562572159-4efc207f5aff", "photo-1520026582657-4daf5bb60adb", "photo-1623288516140-47a0a17cec7c", "photo-1662532577856-e8ee8b138a8b", "photo-1726747109055-b1d02485f790"];
  const productWall = products.slice(0, 6).map((p) => (p.images || [])[0]).filter(Boolean);
  const wallItems = productWall.length ? productWall : fallbackWall.map((pid) => `https://images.unsplash.com/${pid}`);

  return (
    <div className="bg-alabaster">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden pt-28 lg:pt-36">
        <div className="pointer-events-none absolute -right-24 top-24 h-[420px] w-[420px] rounded-full border border-crimson/20" />
        <div className="pointer-events-none absolute -left-32 bottom-0 h-[300px] w-[300px] rounded-full bg-crimson-sub blur-3xl" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 pb-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-24">
          <motion.div style={{ y: yText }} className="flex flex-col justify-center lg:col-span-6">
            <MaskedLine>
              <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Just Kords · New Drop</span>
            </MaskedLine>
            <h1 className="mt-6 font-serif text-5xl font-light leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
              <MaskedLine index={0}>Co-ord sets</MaskedLine>
              <MaskedLine index={1}>for every</MaskedLine>
              <MaskedLine index={2}><em className="text-crimson">mood.</em></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-7 max-w-md text-base leading-relaxed text-mutedc"
            >
              Matching kurta-palazzo sets in breathable muslin, fluid rayon and slub cotton — cut to flatter, made to move. One purchase, infinite looks.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/shop" data-testid="hero-shop-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-crimson-dark"
              >
                Shop the Collection
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/about" data-testid="hero-about-cta"
                className="inline-flex items-center gap-2 rounded-full border border-ink px-8 py-4 text-sm font-semibold transition-colors duration-200 hover:bg-ink hover:text-white"
              >
                Our Story
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedc"
            >
              <span>Free shipping ₹2,999+</span>
              <span className="text-crimson">✦</span>
              <span>Razorpay secured payments</span>
            </motion.div>
          </motion.div>

          <div className="relative lg:col-span-6">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative ml-auto aspect-[4/5] w-full max-w-xl overflow-hidden"
            >
              <motion.img
                style={{ y: yImg }}
                src={img("https://images.unsplash.com/photo-1662532577856-e8ee8b138a8b", 1600)}
                alt="Editorial crimson co-ord set from Just Kords"
                className="absolute inset-0 h-full w-full scale-110 object-cover"
              />
              <div className="absolute inset-0 hidden border border-ink/10 md:block" />
            </motion.div>
            <div className="pointer-events-none absolute -left-4 bottom-16 hidden h-full w-full border-2 border-crimson lg:block" style={{ transform: "translate(-20px, 20px)" }} />
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              data-testid="hero-style-chip"
              className="absolute bottom-6 left-0 flex items-center gap-4 bg-white px-5 py-4 shadow-xl sm:left-6"
            >
              <span className="font-serif text-4xl leading-none text-crimson">{products.length || 30}</span>
              <div>
                <p className="text-sm font-bold leading-none">co-ord styles live</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-mutedc">Kurta & palazzo sets · Sizes M–6XL</p>
              </div>
            </motion.div>
            <p className="absolute right-2 top-6 hidden font-mono text-[10px] uppercase tracking-[0.4em] text-mutedc [writing-mode:vertical-rl] lg:block">
              justkords.com — SS'25
            </p>
          </div>
        </div>
      </section>

      <Marquee items={["Co-ord sets for every mood", "Muslin", "Rayon", "Crepe", "Turkish slub cotton", "Sizes M to 6XL", "Free shipping ₹2,999+"]} />

      {/* BENTO COLLECTIONS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">The Collections</p>
            <h2 className="mt-4 max-w-xl font-serif text-4xl font-light leading-tight sm:text-5xl">
              Four fabrics. One wardrobe philosophy.
            </h2>
          </div>
          <Link to="/shop" data-testid="bento-view-all" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
            View all <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>

        <div className="mt-12 grid auto-rows-[220px] grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 md:grid-rows-2" data-testid="bento-collections">
          {bentoTiles.map((b, i) => (
            <Reveal key={b.label} delay={i * 0.08} className={b.span}>
              <Link
                to={b.to}
                data-testid={`bento-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative block h-full w-full overflow-hidden bg-ink"
              >
                <img
                  src={img(b.img, 1200)} alt={b.label} loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-90 transition-all duration-700 group-hover:scale-105 group-hover:opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">{b.note}</p>
                  <h3 className="mt-2 font-serif text-2xl text-white sm:text-3xl">{b.label}</h3>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white opacity-0 transition-all duration-300 group-hover:opacity-100">
                    Shop now <ArrowRight size={13} />
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="border-y border-linec bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">The Edit</p>
              <h2 className="mt-4 font-serif text-4xl font-light sm:text-5xl">{tagged.length ? "Bestsellers" : "Handpicked For You"}</h2>
            </div>
            <Link to="/shop" data-testid="bestsellers-view-all" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
              Shop all sets <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mx-auto mt-12 flex max-w-7xl gap-6 overflow-x-auto px-4 pb-4 no-scrollbar sm:px-6 lg:px-8" data-testid="bestsellers-row">
          {bestsellers.length === 0 && <p className="py-8 text-sm text-mutedc">Loading the collection…</p>}
          {bestsellers.map((p, i) => (
            <div key={p.slug} className="w-[240px] flex-shrink-0 sm:w-[280px]">
              <ProductCard product={p} index={i} />
            </div>
          ))}
        </div>
      </section>

      {/* CRAFT */}
      <section className="bg-ink py-16 text-alabaster lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">The Craft</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight sm:text-6xl">
              Cut with intention.<br /><em className="text-crimson">Worn without effort.</em>
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
              Every Just Kords set begins as a pattern drafted for real bodies, refined over multiple fittings, and sewn in small batches. No fast-fashion churn — just considered pieces that earn their place in your wardrobe.
            </p>
            <Link to="/about" data-testid="craft-about-link" className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold transition-colors duration-200 hover:border-crimson hover:bg-crimson">
              Read our story <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="flex flex-col justify-center gap-2">
            {[
              ["01", "Luxe fabrics", "Breathable linens, bias-cut satins and fine-gauge knits — sourced for drape first."],
              ["02", "Flatter-first fits", "Every set is tested on real bodies across sizes before it earns a spot in the drop."],
              ["03", "Small batches", "Limited runs mean less waste, more exclusivity — and sets you won't see everywhere."],
            ].map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.1}>
                <div className="flex gap-6 border-b border-white/10 py-8" data-testid={`craft-point-${n}`}>
                  <span className="font-mono text-sm text-crimson">{n}</span>
                  <div>
                    <h3 className="font-serif text-2xl">{t}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-white/55">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM WALL */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal className="text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">@justkords</p>
          <h2 className="mt-4 font-serif text-4xl font-light sm:text-5xl">Styled by you</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="instagram-wall">
          {wallItems.map((item, i) => (
            <Reveal key={item} delay={i * 0.05}>
              <a
                href="https://instagram.com/justkords" target="_blank" rel="noopener noreferrer"
                data-testid={`instagram-wall-item-${i}`}
                className="group relative block aspect-square overflow-hidden bg-[#EFEAE3]"
              >
                <img src={img(item, 600)} alt="Just Kords co-ord set" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <span className="absolute inset-0 flex items-center justify-center bg-crimson/0 text-[11px] font-semibold uppercase tracking-widest text-white opacity-0 transition-all duration-300 group-hover:bg-crimson/70 group-hover:opacity-100">
                  @justkords
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterBand />
    </div>
  );
}

function NewsletterBand() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    try {
      await axios.post(`${API}/newsletter`, { email });
      setDone(true);
    } catch { setDone(true); }
  };

  return (
    <section className="bg-crimson py-16 text-white lg:py-24" data-testid="newsletter-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-white/70">The Kords Club</p>
            <h2 className="mt-4 font-serif text-4xl font-light sm:text-5xl">First dibs on every drop.</h2>
            <p className="mt-4 text-sm leading-relaxed text-white/75">
              Join the club for early access to new sets, members-only pricing and 10% off your first order with code <span className="font-mono font-semibold">JUST10</span>.
            </p>
            {done ? (
              <p data-testid="newsletter-success" className="mt-8 font-serif text-2xl italic">You're in. Welcome to the club.</p>
            ) : (
              <form onSubmit={submit} className="mx-auto mt-8 flex max-w-md overflow-hidden rounded-full bg-white p-1.5">
                <input
                  data-testid="newsletter-email-input"
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-transparent px-5 text-sm text-ink placeholder:text-mutedc focus:outline-none"
                />
                <button data-testid="newsletter-submit" type="submit" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark">
                  Join
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
